import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import request from "../utils/request";
import { setHostInfo } from "../store/slices/hostSlices";

const PENDING_HOSTFULLY_OAUTH_KEY = "pending_hostfully_oauth";
const INTEGRATIONS_TOAST_STORAGE_KEY = "pending_integration_toast";

const HostfullyCallbackScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);

  const [statusText, setStatusText] = useState("Preparing callback...");
  const [errorText, setErrorText] = useState("");
  const startedRef = useRef(false);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const code = (query.get("code") || "").trim();
  const state = (query.get("state") || "").trim();
  const status = (query.get("status") || "").trim();

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (status && status.toUpperCase() !== "SUCCESSFUL") {
      setErrorText(`Hostfully authorization was not successful (${status}).`);
      sessionStorage.removeItem(PENDING_HOSTFULLY_OAUTH_KEY);
      return;
    }

    if (!code || !state) {
      setErrorText("Missing OAuth callback parameters. Please reconnect Hostfully from Integrations.");
      sessionStorage.removeItem(PENDING_HOSTFULLY_OAUTH_KEY);
      return;
    }

    sessionStorage.setItem(
      PENDING_HOSTFULLY_OAUTH_KEY,
      JSON.stringify({ code, state, status, createdAt: Date.now() })
    );

    if (!hostInfo?._id) {
      setStatusText("Sign in required. Redirecting to login...");
      navigate("/login", { replace: true });
      return;
    }

    let cancelled = false;

    const completeCallback = async () => {
      try {
        setStatusText("Connecting Hostfully account...");
        const callbackResponse = await request.post(
          `/hosts/hostfully/oauth-callback/${hostInfo._id}`,
          { code, state, status: status || "SUCCESSFUL" },
          { withCredentials: true }
        );

        setStatusText("Refreshing profile...");
        const { data } = await request.get(`/hosts/${hostInfo._id}`, { withCredentials: true });

        if (cancelled) return;

        localStorage.setItem("hostInfo", JSON.stringify(data));
        dispatch(setHostInfo(data));
        sessionStorage.removeItem(PENDING_HOSTFULLY_OAUTH_KEY);
        sessionStorage.setItem(
          INTEGRATIONS_TOAST_STORAGE_KEY,
          JSON.stringify({
            type: "success",
            message: callbackResponse?.data?.msg || "Hostfully connected successfully.",
          })
        );
        navigate("/integrations", { replace: true });
      } catch (error) {
        if (cancelled) return;

        const statusCode = error?.response?.status;
        const message = error?.response?.data?.message || "Failed to complete Hostfully OAuth callback.";

        sessionStorage.removeItem(PENDING_HOSTFULLY_OAUTH_KEY);

        if (statusCode === 401) {
          setStatusText("Session expired. Redirecting to login...");
          sessionStorage.setItem(
            INTEGRATIONS_TOAST_STORAGE_KEY,
            JSON.stringify({
              type: "error",
              message,
            })
          );
          navigate("/login", { replace: true });
          return;
        }

        setErrorText(message);
      }
    };

    completeCallback();

    return () => {
      cancelled = true;
    };
  }, [code, state, status, hostInfo?._id, navigate, dispatch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white/80 backdrop-blur-xl rounded-2xl border border-white/60 shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Connecting Hostfully</h1>
        {!errorText ? (
          <p className="text-sm text-gray-600">{statusText}</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-red-600">{errorText}</p>
            <div className="flex items-center gap-4">
              <Link to="/integrations" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                Back to Integrations
              </Link>
              <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-gray-900">
                Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostfullyCallbackScreen;
